import { Injectable } from "@angular/core";
import { db, SyncableStatus, Lot, InspectionJob, Component } from "../../../core/database/db";
import { InspectionJobDetail } from "../models/InspectionJobDetail";
import { AreaDTO } from "../../areas/models/AreaDTO";
import { JobSignatureDTO } from "../models/JobSignatureDTO";
import { JobWithLot } from "../models/JobWithLot";
import { ZoneDTO } from "../../zones/models/ZoneDTO";
import { LoggerService } from "../../../shared/services/logger.service";
import { LotDto } from "../../lots/models/LotDto";
import { DateUtilsService } from "../../../shared/services/date.service";
import { BqaMetadataForLoggingWithMetadata } from "../../../core/sync/data-sync/data-sync-metadata.service";
import { InspectionJobDTO } from "../models/InspectionJobDTO";
import { ClosedJobDTO } from "../models/JobScheduleDTO";
import { AssertionDTO } from "../../assertions/models/AssertionDTO";
import { ComponentDTO } from "../../components/models/ComponentDTO";
import { CONSTANTS } from "../../../constants";

export const SyncStatusIdentifiers = {
  CustomerCareManager: "CustomerCareManager",
  ConstructionManager: "ConstructionManager",
};

@Injectable({
  providedIn: "root",
})
export default class JobDataService {
  private cleanupDebounceTimer: number | null = null;
  private isCleanupInProgress: boolean = false;
  constructor(
    private logger: LoggerService,
    private dateUtils: DateUtilsService
  ) { }

  async getInspectionJobDetailByJobId(jobId: number): Promise<InspectionJobDetail | undefined> {
    const job = await db.inspectionJob.get(jobId);

    if (!job) {
      return undefined;
    }

    const lot = await db.lot.get(job.lotId);

    if (!lot) {
      return undefined;
    }

    return {
      ...lot,
      ...job,
      streetAddress: job.streetAddress ?? lot.streetAddress,
    };
  }

  async getLastCustomerCareManagerSync() {
    const lastCompletedSync = await db.syncStatus.get(SyncStatusIdentifiers.CustomerCareManager);
    return lastCompletedSync?.lastCompletedSync;
  }

  async customerCareManagerSyncCompleted() {
    await db.syncStatus.put({
      id: SyncStatusIdentifiers.CustomerCareManager,
      lastCompletedSync: new Date()
    });
  }

  async syncLotData(lotData: LotDto[]) {
    const lotsFromApi = lotData.filter(lot => !!lot && !!lot.id);
    const apiLotsMap = new Map(lotsFromApi.map(lot => [lot.id, lot]));

    const mapLotFields = (lot: LotDto): Lot => {
      const { zones: _zones, media: _media, ...lotFields } = lot;
      return {
        ...lotFields,
        updatedAt: new Date(lot.updatedAt),
        closeOfEscrow: lot.closeOfEscrow
          ? this.dateUtils.convertUtcDateToLocalDateOnly(lot.closeOfEscrow)
          : undefined,
        // Server data is always considered synced; a locally pending lot photo change is re-applied below.
        syncStatus: SyncableStatus.Completed
      } as Lot;
    };

    await db.transaction("rw", [db.lot, db.syncStatus], async () => {
      db.setSuppressHooks(true);
      const localLots = await db.lot
        .where("id")
        .anyOf(lotsFromApi.map(lot => lot.id))
        .toArray();

      const localLotIds = new Set(localLots.map(lot => lot.id));
      const currentSyncStatusByLotId = new Map(localLots.map(lot => [lot.id, lot.syncStatus]));

      const newLots = lotsFromApi.filter(apiLot => !localLotIds.has(apiLot.id)).map(mapLotFields);

      const updateLots: Lot[] = [];
      for (const localLot of localLots) {
        const apiLot = apiLotsMap.get(localLot.id);
        if (apiLot && new Date(localLot.updatedAt).getTime() < new Date(apiLot.updatedAt).getTime()) {
          updateLots.push(mapLotFields(apiLot));
        }
      }

      if (newLots.length > 0) {
        await db.batchBulkOperation("lot:add", newLots, async newLots => {
          await db.lot.bulkAdd(newLots);
        }, this.logger);
      }

      if (updateLots.length > 0) {
        await db.batchBulkOperation("lot:update", updateLots, async updateLots => {
          await db.lot.bulkUpdate(updateLots.map(lot => ({
            key: lot.id,
            changes: {
              ...lot,
              // Don't clobber a lot photo change that's still waiting to be synced up.
              syncStatus: currentSyncStatusByLotId.get(lot.id) === SyncableStatus.Pending
                ? SyncableStatus.Pending
                : lot.syncStatus
            }
          })));
        }, this.logger);
      }
    });
  }

  async syncJobData(jobData: InspectionJobDTO[]) {
    const jobDataFromApi = jobData.filter(j => !!j && !!j.id);
    const apiJobsMap = new Map(jobDataFromApi.map(job => [job.id, job]));

    await db.transaction("rw", [db.inspectionJob], async () => {
      db.setSuppressHooks(true);
      const localJobs = await db.inspectionJob
        .where("id")
        .anyOf(jobDataFromApi.map(j => j.id))
        .toArray();

      const localJobIds = new Set(localJobs.map(j => j.id));
      const localJobsById = new Map(localJobs.map(job => [job.id, job]));

      const newJobs = jobDataFromApi.filter(apiJob => !localJobIds.has(apiJob.id));

      const updateJobs: (InspectionJobDTO & { id: number })[] = [];
      for (const localJob of localJobs) {
        const apiJob = apiJobsMap.get(localJob.id);
        if (!apiJob) {
          continue;
        }

        // A submitted job can never become unsubmitted, so another device's submission wins even when this
        // device has pending edits - otherwise the Pending guard below strands it as "in progress" forever.
        const submittedElsewhere = apiJob.isComplete && !!apiJob.submittedAt && !localJob.isComplete;

        // Only update if the API job is newer than the local job
        const apiJobIsNewer =
          localJob.syncStatus !== SyncableStatus.Pending &&
          new Date(localJob.updatedAt).getTime() < new Date(apiJob.updatedAt).getTime();

        if (submittedElsewhere || apiJobIsNewer) {
          updateJobs.push({
            ...apiJob,
            id: localJob.id!,
          });
        }
      }

      const jobsToAdd: InspectionJob[] = newJobs.map<InspectionJob>(dto => ({
        id: dto.id,
        lotId: dto.lotId,
        scheduledDate: this.dateUtils.convertUtcDateToLocalDateOnly(dto.scheduledDate),
        isComplete: dto.isComplete,
        updatedAt: new Date(dto.updatedAt),
        submittedAt: dto.submittedAt ? new Date(dto.submittedAt) : undefined,
        score: dto.score,
        buildQualityStep: dto.buildQualityStep,
        streetAddress: dto.streetAddress,
        updatedBy: dto.updatedBy,
        divisionId: dto.divisionId,
        startedAt: dto.startedAt ? new Date(dto.startedAt) : undefined,
        syncStatus: SyncableStatus.Completed
      }));

      if (jobsToAdd.length > 0) {
        await db.batchBulkOperation("inspectionJob:add", jobsToAdd, async jobsToAdd => {
          await db.inspectionJob.bulkAdd(jobsToAdd);
        }, this.logger);
      }

      if (updateJobs.length > 0) {
        await db.batchBulkOperation("inspectionJob:update", updateJobs, async updateJobs => {
          await db.inspectionJob.bulkUpdate(updateJobs.map(j => {
            const localJob = localJobsById.get(j.id);

            const changes: Partial<InspectionJob> = {
              scheduledDate: this.dateUtils.convertUtcDateToLocalDateOnly(j.scheduledDate),
              updatedAt: new Date(j.updatedAt),
              score: j.score
            };

            // Check if write-once fields are protected (checking for both null and undefined)
            const isLocalComplete = localJob?.isComplete === true && !!localJob?.submittedAt;

            if (!isLocalComplete) {
              changes.isComplete = j.isComplete;
              changes.submittedAt = j.submittedAt ? new Date(j.submittedAt) : undefined;
            }

            const isLocalStarted = !!localJob?.startedAt;

            if (!isLocalStarted) {
              changes.startedAt = j.startedAt ? new Date(j.startedAt) : undefined;
            }

            return {
              key: j.id,
              changes
            };
          }));
        }, this.logger);
      }
    });
  }

  async syncZoneData(zoneData: ZoneDTO[]) {
    const zonesFromApi = zoneData.filter(zone => !!zone && !!zone.id);

    await db.transaction("rw", [db.zone, db.syncStatus], async () => {
      db.setSuppressHooks(true);
      const localZoneData = await db.zone
        .where("id")
        .anyOf(zonesFromApi.map(z => z.id))
        .toArray();

      const newZones = zonesFromApi.filter(i => !localZoneData.find(z => z.id === i.id));
      const updateZones: (ZoneDTO & { id: number })[] = localZoneData.map(localZone => {
        const apiZone = zonesFromApi.find(i => i.id === localZone.id);

        if (apiZone
          && localZone.syncStatus !== SyncableStatus.Pending
          && new Date(localZone.updatedAt).getTime() < new Date(apiZone.updatedAt).getTime()
        ) {
          return {
            ...apiZone,
            id: localZone.id!,
          };
        }

        return null;
      }).filter(i => !!i);

      if (newZones.length > 0) {
        await db.batchBulkOperation("zone:add", newZones, async newZones => {
          await db.zone.bulkAdd(newZones.map(z => ({
            ...z,
            syncStatus: SyncableStatus.Completed,
          })));
        }, this.logger);
      }

      if (updateZones.length > 0) {
        await db.batchBulkOperation("zone:update", updateZones, async updateZones => {
          await db.zone.bulkUpdate(updateZones.map(z => ({
            key: z.id,
            changes: {
              ...z,
              syncStatus: SyncableStatus.Completed,
            }
          })));
        }, this.logger);
      }
    });
  }

  async syncAreaData(areaData: {
    area: AreaDTO,
    zoneId: number
  }[]): Promise<{ [key: string]: number }> {
    const areasFromApi = areaData.filter(area => !!area && !!area.area.identifier);

    let insertedAreas: number[] = [];
    let newAreasForReturn: typeof areasFromApi = [];

    await db.transaction("rw", [db.area, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      const localAreaData = await db.area
        .where("identifier")
        .anyOf(areasFromApi.map(z => z.area.identifier))
        .toArray();

      const newAreas = areasFromApi
        .filter(i => !localAreaData.find(a => a.identifier === i.area.identifier));
      newAreasForReturn = newAreas;

      const updateAreas: (AreaDTO & { id: number, zoneId: number })[] = localAreaData.map(localData => {
        const apiArea = areasFromApi.find(i => i.area.identifier === localData.identifier);

        if (apiArea
          && localData.syncStatus !== SyncableStatus.Pending
          && new Date(localData.updatedAt).getTime() < new Date(apiArea.area.updatedAt).getTime()
        ) {
          return {
            ...apiArea.area,
            id: localData.id!,
            zoneId: localData.zoneId
          };
        }

        return false;
      }).filter(i => !!i);

      if (newAreas.length > 0) {
        insertedAreas = await db.batchBulkOperation("area:add", newAreas, async newAreas => {
          return await db.area.bulkAdd(newAreas.map(a => ({
            ...a.area,
            zoneId: a.zoneId,
            syncStatus: SyncableStatus.Completed,
          })), { allKeys: true });
        }, this.logger);
      }

      if (updateAreas.length > 0) {
        await db.batchBulkOperation("area:update", updateAreas, async updateAreas => {
          await db.area.bulkUpdate(updateAreas.filter(i => i.id).map(a => {
            const { name: _name, id: _id, identifier: _identifier, components: _components, ...changesWithoutName } = a;
            return ({
              key: a.id!,
              changes: {
                ...changesWithoutName,
                syncStatus: SyncableStatus.Completed,
              }
            });
          }));
        }, this.logger);
      }
    });

    return insertedAreas.reduce((dict, areaId, index) => {
      const identifier = newAreasForReturn[index].area.identifier;
      dict[identifier] = areaId;
      return dict;
    }, {} as { [key: string]: number });
  }

  async syncComponentData(
    componentData: { component: ComponentDTO, areaIdentifier: string, lotId: number }[]
  ) {
    const componentDataFromApi = componentData.filter(c => !!c && !!c.component.identifier);
    const distinctComponentsFromApi = componentDataFromApi.filter((value, index, self) =>
      index === self.findIndex(c => c.component.identifier === value.component.identifier)
    );

    let insertedComponents: number[] = [];
    let newComponentsForReturn: typeof distinctComponentsFromApi = [];
    let localComponentDataForReturn: Component[] = [];

    await db.transaction("rw", [db.component, db.area, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      const localComponentData = await db.component.where("identifier").anyOf(distinctComponentsFromApi.map(c => c.component.identifier))
        .toArray();
      localComponentDataForReturn = localComponentData;

      const areas = await db.area.where("identifier").anyOf(distinctComponentsFromApi.map(i => i.areaIdentifier))
        .toArray();
      const areaIdentifiersToId = areas.reduce((map, current) => {
        map[current.identifier] = current.id;
        return map;
      }, {} as { [key: string]: number | undefined });

      const newComponents = distinctComponentsFromApi
        .filter(i => !localComponentData.find(c =>
          c.identifier === i.component.identifier)
        );
      newComponentsForReturn = newComponents;

      const updateComponents = localComponentData.map(localComponent => {
        const apiComponent = distinctComponentsFromApi
          .find(c => c.component.identifier === localComponent.identifier);

        if (apiComponent
          && localComponent.syncStatus !== SyncableStatus.Pending
          && new Date(localComponent.updatedAt).getTime()
          < new Date(apiComponent.component.updatedAt).getTime()
        ) {
          return {
            ...apiComponent,
            lotId: localComponent.lotId || apiComponent.lotId,
            id: localComponent.id,
          };
        }

        return null;
      }).filter(i => !!i);

      if (newComponents.length > 0) {
        insertedComponents = await db.batchBulkOperation("component:add", newComponents, async newComponents => {
          return await db.component.bulkAdd(newComponents.map(c => ({
            ...c.component,
            areaId: areaIdentifiersToId[c.areaIdentifier]!,
            lotId: c.lotId,
            syncStatus: SyncableStatus.Completed,
          })), { allKeys: true });
        }, this.logger);
      }

      if (updateComponents.length > 0) {
        await db.batchBulkOperation("component:update", updateComponents, async updateComponents => {
          await db.component.bulkUpdate(updateComponents.filter(i => i.id).map(c => {
            const { component, areaIdentifier: _areaIdentifier, id: _id, ..._rest } = c;
            const { name: _name, identifier: _identifier, assertions: _assertions, ...componentChangesWithoutName } = component;
            return ({
              key: c.id!,
              changes: {
                ...componentChangesWithoutName,
                areaId: areaIdentifiersToId[c.areaIdentifier]!,
                syncStatus: SyncableStatus.Completed,
              }
            });
          }));
        }, this.logger);
      }
    });

    return {
      insertedComponents: insertedComponents.reduce((dict, componentId, index) => {
        const identifier = newComponentsForReturn[index].component.identifier;
        dict[identifier] = componentId;
        return dict;
      }, {} as { [key: string]: number }),
      existingComponents: localComponentDataForReturn.reduce((dict, component) => {
        dict[component.identifier] = component.id!;
        return dict;
      }, {} as { [key: string]: number })
    };
  }

  async syncAssertionData(
    assertionData: {
      assertion: AssertionDTO,
      componentId: number,
      lotId: number
    }[]) {
    const assertionDataFromApi = assertionData.filter(a => !!a && !!a.assertion.identifier);
    const distinctAssertionsFromApi = assertionDataFromApi.filter((value, index, self) =>
      index === self.findIndex(a => a.assertion.identifier === value.assertion.identifier)
    );

    let insertedAssertions: number[] = [];
    let newAssertionsForReturn: typeof distinctAssertionsFromApi = [];

    await db.transaction("rw", [db.assertion, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      const localAssertionData = await db.assertion
        .where("identifier")
        .anyOf(distinctAssertionsFromApi.map(a => a.assertion.identifier))
        .toArray();

      const localAssertionMap = new Map(
        localAssertionData.map(assert => [assert.identifier, assert])
      );

      const newAssertions: { assertion: AssertionDTO, componentId: number, lotId: number }[] = [];
      const updateAssertions: (typeof newAssertions[number] & { id: number | undefined })[] = [];

      for (const data of distinctAssertionsFromApi) {
        const localAssertion = localAssertionMap.get(data.assertion.identifier);

        if (!localAssertion) {
          newAssertions.push(data);
        } else if (localAssertion.lotId === data.lotId &&
          localAssertion.syncStatus !== SyncableStatus.Pending &&
          new Date(localAssertion.updatedAt).getTime() < new Date(data.assertion.updatedAt).getTime()) {
          updateAssertions.push({
            ...data,
            id: localAssertion.id,
            lotId: localAssertion.lotId,
            componentId: localAssertion.componentId
          });
        }
      }
      newAssertionsForReturn = newAssertions;

      if (newAssertions.length > 0) {
        insertedAssertions = await db.batchBulkOperation("assertion:add", newAssertions, async newAssertions => {
          return await db.assertion.bulkAdd(newAssertions.map(a => ({
            ...a.assertion,
            componentId: a.componentId,
            lotId: a.lotId,
            syncStatus: SyncableStatus.Completed,
          })), { allKeys: true });
        }, this.logger);
      }

      if (updateAssertions.length > 0) {
        await db.batchBulkOperation("assertion:update", updateAssertions, async updateAssertions => {
          await db.assertion.bulkUpdate(updateAssertions.filter(i => i.id).map(a => {
            const { assertion, id: _id, ..._rest } = a;
            const { text: _text, identifier: _identifier, issues: _issues, media: _media, ...assertionChangesWithoutText } = assertion;
            return ({
              key: a.id!,
              changes: {
                ...assertionChangesWithoutText,
                componentId: a.componentId,
                lotId: a.lotId,
                syncStatus: SyncableStatus.Completed,
              }
            });
          }));
        }, this.logger);
      }
    });

    return insertedAssertions.reduce((dict, assertionId, index) => {
      const identifier = newAssertionsForReturn[index].assertion.identifier;
      dict[identifier] = assertionId;
      return dict;
    }, {} as { [key: string]: number });
  }

  async syncSignatureData(signatureData: JobSignatureDTO[]) {
    const signaturesFromApi = signatureData.filter(s => !!s && !!s.identifier);

    await db.transaction("rw", [db.jobSignature], async () => {
      db.setSuppressHooks(true);
      const localSignatureData = await db.jobSignature
        .where("identifier")
        .anyOf(signaturesFromApi.map(s => s.identifier))
        .toArray();

      const newSignatures = signaturesFromApi.filter(i =>
        !localSignatureData.find(s => s.identifier === i.identifier)
      );
      const updateSignatures: (JobSignatureDTO & { id: number })[] = localSignatureData.map(localSignature => {
        const apiSignature = signaturesFromApi.find(i => i.identifier === localSignature.identifier);
        if (apiSignature
          && localSignature.syncStatus !== SyncableStatus.Pending
          && new Date(localSignature.updatedAt).getTime() < new Date(apiSignature.updatedAt).getTime()
        ) {
          return {
            ...apiSignature,
            id: localSignature.id!,
          };
        }

        return null;
      }).filter(i => !!i);

      if (newSignatures.length > 0) {
        await db.batchBulkOperation("jobSignature:add", newSignatures, async newSignatures => {
          await db.jobSignature.bulkAdd(newSignatures.map(s => ({
            ...s,
            signedAt: s.signedAt ? new Date(s.signedAt) : undefined,
            identifier: s.identifier,
            syncStatus: SyncableStatus.Completed,
          })));
        }, this.logger);
      }

      if (updateSignatures.length > 0) {
        await db.batchBulkOperation("jobSignature:update", updateSignatures, async updateSignatures => {
          await db.jobSignature.bulkUpdate(updateSignatures.map(s => ({
            key: s.id,
            changes: {
              ...s,
              signedAt: s.signedAt ? new Date(s.signedAt) : undefined,
              syncStatus: SyncableStatus.Completed,
            }
          })));
        }, this.logger);
      }
    });
  }

  /**
   * Corrects locally cached jobs the server reports as submitted but deliberately omits from the schedule
   * payload.
   */
  async reconcileClosedJobs(closedJobs: ClosedJobDTO[]): Promise<void> {
    if (!closedJobs?.length) {
      return;
    }

    const localJobs = await db.inspectionJob
      .where("id").anyOf(closedJobs.map(j => j.id))
      .toArray();

    if (localJobs.length === 0) {
      return;
    }

    const submittedAtById = new Map(closedJobs.map(j => [j.id, new Date(j.submittedAt)]));
    const staleJobs = localJobs.filter(job => !job.isComplete || !job.submittedAt);

    if (staleJobs.length === 0) {
      return;
    }

    this.logger.warn(
      `[Sync] Correcting locally stale jobs [${staleJobs.map(j => j.id).join(", ")}] that the server reports as submitted.`
    );

    await db.inspectionJob.bulkUpdate(staleJobs.map(job => ({
      key: job.id,
      changes: {
        isComplete: true,
        submittedAt: submittedAtById.get(job.id),
        syncStatus: SyncableStatus.Completed
      }
    })));
  }

  async cleanupStaleLots(serverLotIds: Set<number>): Promise<void> {
    const allLocalLots = await db.lot.toArray();
    const staleLots = allLocalLots.filter(lot => !serverLotIds.has(lot.id));

    if (staleLots.length === 0) {
      return;
    }

  }

  async cleanupSupersededSameLotJobs(incomingJobs: JobWithLot[]): Promise<void> {
    const incomingBq7Jobs = incomingJobs.filter(
      job => job.buildQualityStep === CONSTANTS.BUILD_QUALITY_STEPS.BQ7
    );

    if (incomingBq7Jobs.length === 0) {
      return;
    }

    const serverJobByLotId = new Map(incomingBq7Jobs.map(job => [job.lotId, job]));
    const localBq7Jobs = await db.inspectionJob
      .where("buildQualityStep").equals(CONSTANTS.BUILD_QUALITY_STEPS.BQ7)
      .toArray();

    const idsToDelete: number[] = [];

    for (const localJob of localBq7Jobs) {
      const serverJob = serverJobByLotId.get(localJob.lotId);

      if (!serverJob || serverJob.id === localJob.id) {
        continue;
      }

      if (localJob.syncStatus === SyncableStatus.Pending) {
        continue;
      }

      if (await this.hasJobScopedUserData(localJob.id)) {
        this.logger.info(`[Sync] Skipping superseded job cleanup for Job ${localJob.id}: Has job-scoped user data.`);
        continue;
      }

      idsToDelete.push(localJob.id);
    }

    if (idsToDelete.length > 0) {
      this.logger.warn(
        `[Sync] Cleaning up superseded same-lot BQ7 jobs [${idsToDelete.join(", ")}].`
      );
      await this.bulkDeleteJobs(idsToDelete);
    }
  }

  async cleanupStaleJobs(incomingJobs: JobWithLot[], isCm: boolean = false) {
    // Identify Candidates (Local Jobs NOT in Server Payload)
    const serverIds = new Set(incomingJobs.map(j => j.id));
    const serverJobByLotId = new Map(incomingJobs.map(job => [job.lotId, job]));
    const cmJobBuildQualitySteps = CONSTANTS.BUILD_QUALITY_STEPS.ALL;
    const allLocalJobs = isCm
      ? await db.inspectionJob
        .where("buildQualityStep").anyOf(cmJobBuildQualitySteps)
        .toArray()
      : await db.inspectionJob
        .where("buildQualityStep").equals(CONSTANTS.BUILD_QUALITY_STEPS.BQ7)
        .toArray();

    const orphanCandidates = allLocalJobs.filter(job => !serverIds.has(job.id) && job.syncStatus !== SyncableStatus.Pending);

    if (orphanCandidates.length === 0) {
      return;
    }

    const idsToDelete: number[] = [];

    // Safety Check: Only delete if NO pending data exists
    for (const job of orphanCandidates) {
      if (job.syncStatus === "pending") {
        continue;
      }

      const serverJobForLot = serverJobByLotId.get(job.lotId);
      const isSupersededByServerJob = !!serverJobForLot && serverJobForLot.id !== job.id;

      if (isSupersededByServerJob) {
        if (await this.hasJobScopedUserData(job.id)) {
          this.logger.info(`[Sync] Skipping superseded orphan cleanup for Job ${job.id}: Has job-scoped user data.`);
          continue;
        }

        idsToDelete.push(job.id);
        continue;
      }

      const [
        hasJobIssues,
        hasAssertionIssues,
        hasSignatures,
        hasLooksGoodAssertions,
        hasNotApplicableAssertions,
      ] = await Promise.all([
        // Issues are always user data
        db.jobIssue.where("jobId").equals(job.id)
          .count(),
        db.assertionIssue.where("jobId").equals(job.id)
          .count(),
        db.jobSignature.where("jobId").equals(job.id)
          .count(),
        // Assertions are only user data if they have been answered
        db.assertion
          .where("lotId").equals(job.lotId)
          .filter(a => (a.isComplete || a.isCcmComplete))
          .count(),
        db.assertion
          .where("lotId").equals(job.lotId)
          .filter(a => a.isApplicable === false)
          .count()
      ]);

      if (hasJobIssues > 0 || hasAssertionIssues > 0 || hasSignatures > 0 || hasLooksGoodAssertions > 0 || hasNotApplicableAssertions > 0) {
        this.logger.info(`[Sync] Skipping cleanup for Job ${job.id}: Has user data.`);
        continue;
      }

      idsToDelete.push(job.id);
    }

    if (idsToDelete.length > 0) {
      this.logger.warn(`[Sync] Cleaning up jobs [${idsToDelete.join(", ")}], not present on API response and no user data found.`);
      await this.bulkDeleteJobs(idsToDelete);
    }
  }

  async startJob(inspectionJob: InspectionJob) {
    if (!inspectionJob.startedAt) {
      const now = new Date();

      inspectionJob.startedAt = now;
      inspectionJob.updatedAt = now;
      inspectionJob.syncStatus = SyncableStatus.Pending;

      await db.inspectionJob.update(inspectionJob.id, {
        startedAt: now,
        updatedAt: now,
        syncStatus: SyncableStatus.Pending,
      });
    }

    return inspectionJob;
  }

  private async hasJobScopedUserData(jobId: number): Promise<boolean> {
    const [hasJobIssues, hasAssertionIssues, hasSignatures] = await Promise.all([
      db.jobIssue.where("jobId").equals(jobId)
        .count(),
      db.assertionIssue.where("jobId").equals(jobId)
        .count(),
      db.jobSignature.where("jobId").equals(jobId)
        .count(),
    ]);

    return hasJobIssues > 0 || hasAssertionIssues > 0 || hasSignatures > 0;
  }

  private async bulkDeleteJobs(jobIds: number[]) {
    const distinctJobIds = new Set(jobIds);
    const jobs = await db.inspectionJob
      .where("id")
      .anyOf(jobIds)
      .toArray();
    const lotIds = [...new Set(jobs.map(j => j.lotId))];

    // Components/assertions are lot-scoped and shared across jobs. Only delete this
    // structural data for lots that will have no remaining jobs after this cleanup.
    const lotIdsForLotsWithNoOtherJobs: number[] = [];
    for (const lotId of lotIds) {
      const remainingJobsCount = await db.inspectionJob
        .where("lotId")
        .equals(lotId)
        .filter(j => !distinctJobIds.has(j.id))
        .count();

      if (remainingJobsCount === 0) {
        lotIdsForLotsWithNoOtherJobs.push(lotId);
      }
    }

    const components = lotIdsForLotsWithNoOtherJobs.length > 0
      ? await db.component
        .where("lotId")
        .anyOf(lotIdsForLotsWithNoOtherJobs)
        .toArray()
      : [];
    const assertions = lotIdsForLotsWithNoOtherJobs.length > 0
      ? await db.assertion
        .where("lotId")
        .anyOf(lotIdsForLotsWithNoOtherJobs)
        .toArray()
      : [];

    const componentIdsToDelete = components.filter(c => !!c.id).map(c => c.id!);
    const assertionIdsToDelete = assertions.filter(a => !!a.id).map(a => a.id!);

    await db.transaction("rw", [
      db.inspectionJob,
      db.component,
      db.assertion,
      db.assertionIssue,
      db.jobIssue,
      db.jobSignature,
      db.media,
      db.syncStatus,
    ], async () => {
      await Promise.all([
        db.inspectionJob.bulkDelete(jobIds),
        db.component.bulkDelete(componentIdsToDelete),
        db.assertion.bulkDelete(assertionIdsToDelete),
        db.assertionIssue.where("jobId").anyOf(jobIds)
          .delete(),
        db.jobIssue.where("jobId").anyOf(jobIds)
          .delete(),
        db.jobSignature.where("jobId").anyOf(jobIds)
          .delete(),
      ]);
    });
  }

  async cleanupFullySyncedJobsOlderThanBusinessDays(
    fullySyncedJobs: BqaMetadataForLoggingWithMetadata[],
    businessDaysThreshold: number
  ) {
    if (!!navigator.onLine === false) {
      return;
    }
    if (!fullySyncedJobs || fullySyncedJobs.length === 0) {
      return;
    }

    if (this.cleanupDebounceTimer) {
      clearTimeout(this.cleanupDebounceTimer);
    }

    this.cleanupDebounceTimer = window.setTimeout(() => {
      this.cleanupDebounceTimer = null;
      this.runCleanup(fullySyncedJobs, businessDaysThreshold)
        .catch(err => this.logger.error("[Cleanup] Cleanup pass failed.", err));
    }, 1000);
  }

  private async runCleanup(
    fullySyncedJobs: BqaMetadataForLoggingWithMetadata[],
    businessDaysThreshold: number
  ) {
    if (this.isCleanupInProgress) {
      return;
    }
    this.isCleanupInProgress = true;

    try {
      for (const syncedJob of fullySyncedJobs) {
        const jobId = Number(syncedJob.jobId);
        if (Number.isFinite(jobId) === false) {
          continue;
        }
        const job = await db.inspectionJob.get(jobId);
        if (!!job === false) {
          continue;
        }

        // Safety gates (AC2/AC6)
        if (job.syncStatus === SyncableStatus.Pending) {
          continue;
        }
        if (job.isComplete === false) {
          continue;
        }
        if (job.buildQualityStep === 8) {
          continue;
        }
        if (!!job.submittedAt === false) {
          continue;
        }
        const anchorRaw = job.submittedAt;
        const submittedDate = anchorRaw instanceof Date ? anchorRaw : new Date(anchorRaw);

        const threeBusinessDayCutoff = this.dateUtils.addBusinessDays(new Date(), -3);
        threeBusinessDayCutoff.setHours(0, 0, 0, 0);

        if (submittedDate > threeBusinessDayCutoff) {
          continue;
        }

        const ageBusinessDays = this.dateUtils.businessDaysDiff(submittedDate, new Date());

        // Must be a scoped delete (do NOT delete by lotId)
        const deleted = await db.deleteCompletedByJobId(jobId, this.logger);

        if (deleted) {
          this.logger.event("Cleanup:JobDeleted", {
            jobId,
            bqStep: job.buildQualityStep,
            jobAddress: job.streetAddress,
            jobLot: job.lotId,
            submittedAt: submittedDate.toISOString(),
            ageBusinessDays,
            threshold: businessDaysThreshold
          });
        } else {
          this.logger.info(`[Cleanup] Job ${jobId} not deleted - pending data/gating conditions.`);
        }

        // yield (AC4)
        await new Promise<void>(r => setTimeout(r, 0));
      }
    } finally {
      this.isCleanupInProgress = false;
    }
  }
}